import { Formik } from 'formik';
import { isEmpty, omit } from 'lodash';
import { object, string } from 'yup';
import PropTypes from 'prop-types';
import React from 'react';
import { classify, singularize, underscore } from 'inflected';

import { patchCollection } from '../../api';
import { patch, prepareAttrs } from '../../../utils/api';
import { useEntity, useEntityActionCreators } from '../../../entities/hooks';
import { useMatch } from '../../../app/hooks';
import FormBody from './form';
import { postMedium } from 'story-media/api';
import { uploadMedium } from 'story-media/upload-medium';
import { useDispatch } from 'react-redux';
import { receiveEntity, updateEntity } from 'entities/actions';

const EditChildJournal = ({ onSuccess, isFromTft }) => {
  const {
    params: { id },
  } = useMatch();
  const type = 'child-journals';
  const { receive } = useEntityActionCreators();
  const { entity: collection } = useEntity({ type, id });

  const dispatch = useDispatch();

  const { entity: avatarEntity } = useEntity({ type: 'images', id: collection?.['subject-avatar']?.id })

  if (isEmpty(collection)) return false;

  return (
    <Formik
      initialValues={{
        'first-name-noac': collection['first-name'],
        'last-name-noac': collection['last-name'],
        'date-of-birth': collection['date-of-birth'] || '',
        cover: '',
        gender: collection?.gender || ''
      }}
      onSubmit={async ({ cover, 'avatar-canvas-image': avatarCanvasImage, ...values }, { setSubmitting }) => {
        const attrs = prepareAttrs(values);

        let uploadParams = {
          'lock-version': avatarEntity?.['lock-version'],
        };

        let updatedMediaId = collection['subject-avatar'] && collection['subject-avatar'].id;
        if (cover instanceof Blob) {
          const ownerType = 'Member'

          const payload = await postMedium({
            attrs: {
              type: 'Image',
              ownerId: collection?.['subject-id'],
              filename: cover.name || 'cover',
              previewFilename: ' ',
              ownerType,
            },
          })
          dispatch(receiveEntity({ payload }));

          const { entities, result } = payload.data;
          const image = entities.images[result.images[0]];
          updatedMediaId = image.id;
          const tmpUrl = URL.createObjectURL(cover);
          dispatch(
            updateEntity({
              entityType: 'images',
              id: image.id,
              attrs: { tmpUrl },
            })
          );
          uploadParams = {
            'lock-version': image['lock-version'],
          };
          // Deliberately not awaited -- the form should not block on S3 -- but now
          // retried, reported, and unable to leave an unhandled rejection.
          uploadMedium({ medium: image, mediumType: 'Image', file: cover });
        }

        if (avatarCanvasImage?.file instanceof Blob) {
          const { file, ...avatarParams } = avatarCanvasImage;
          const payload = await postMedium({
            attrs: {
              type: 'Image',
              ownerId: updatedMediaId,
              ownerType: 'Medium',
              filename: file.name || 'cover',
              ...avatarParams
            },
          })
          dispatch(receiveEntity({ payload }));
          const { entities, result } = payload.data;
          const image = entities.images[result.images[0]];
          const tmpUrl = URL.createObjectURL(file);
          dispatch(
            updateEntity({
              entityType: 'images',
              id: image.id,
              attrs: { tmpUrl },
            })
          );
          uploadParams = {
            ...uploadParams,
            'canvas-image-id': image.id,
            ...avatarParams
          };
          uploadMedium({ medium: image, mediumType: 'Image', file });
          if (updatedMediaId) {
            dispatch(
              updateEntity({
                entityType: 'images',
                id: updatedMediaId,
                attrs: { 'canvas-image': { id: uploadParams?.['canvas-image-id'], type: 'images' } },
              })
            );
            await patch(`/v2/media/${updatedMediaId}`, {
              data: {
                attributes: uploadParams
              }
            })
          }

          const attrs =
            type === 'child-journals'
              ? { 'subject-avatar-id': updatedMediaId, 'canvas-image': { id: image.id, type: 'images' }, 'subject-avatar': { id: updatedMediaId, type: 'images' }, }
              : { 'cover-image': { id: updatedMediaId, type: 'images' } };

          dispatch(
            updateEntity({
              entityType: type,
              attrs,
              id,
            })
          );
        }

        const payload = await patchCollection({
          type,
          id,
          attributes: { id: collection.id, 'subject-avatar-id': updatedMediaId, ...attrs },
        });
        const updatedChildJournal = Object.values(payload.data.entities['child-journals'])[0];
        payload.data.entities['child-journals'][updatedChildJournal.id] = omit(
          updatedChildJournal,
          ['subject-avatar-id', 'subject-avatar']
        );
        receive({ payload });
        setSubmitting(false);
        onSuccess();
      }}
      validationSchema={
        object().shape(isFromTft ? {
          'first-name-noac': string().required('First Name is required'),
          'date-of-birth': string().required('Date of birth is required'),
          gender: string().required('Gender is required'),
        } : {
          'first-name-noac': string().required('First Name is required'),
        })
      }
      render={formikProps => <FormBody isFromTft={isFromTft} {...formikProps} collection={collection} type={type} />}
    />
  );
};

EditChildJournal.propTypes = {
  onSuccess: PropTypes.func,
};

EditChildJournal.defaultProps = {
  onSuccess: () => { },
};

export default EditChildJournal;
